import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import _ from 'lodash';
import { ReplyPayload, Reply } from '../../schema';
import {
  notificationAtom,
  NotificationType,
  pageLoadingAtom,
} from '../../atoms/system';
import { useSetAtom } from 'jotai';
import api from '../../api';
import { IResponse, CommentStatus } from '../../types';
import FormReply from './Form.Reply';
import { Updater, useImmer } from 'use-immer';
import cuid from 'cuid';
import { useEffect } from 'react';

const Create = ({
  prefix_uri,
  parent_id,
  target_id,
  actionReload,
}: {
  prefix_uri: string;
  parent_id: string;
  target_id: string;
  actionReload: Updater<{
    load: string;
    trigger: boolean;
  }>;
}): JSX.Element => {
  const notification = useSetAtom(notificationAtom);
  const pageLoading = useSetAtom(pageLoadingAtom);
  const form = useForm<ReplyPayload>({
    resolver: zodResolver(Reply),
    defaultValues: {
      target_id: target_id,
      status: CommentStatus.active,
      desc: '',
      name: '',
      email: '',
      phone: '',
    },
  });
  form.setValue('target_id', target_id);
  const [asModerator, setAsModerator] = useImmer<boolean>(false);
  const onSubmit = async (data: ReplyPayload) => {
    try {
      pageLoading(true);
      await api.post(`api/${prefix_uri}/${parent_id}/replies`, {
        json: data,
      });

      notification({
        show: true,
        type: NotificationType.success,
        title: 'Tạo mới thành công',
      });
      actionReload(draft => {
        draft.load = cuid();
        draft.trigger = true;
      });
      form.setValue('desc', '');
      if (!asModerator) {
        form.setValue('name', '');
        form.setValue('email', '');
        form.setValue('phone', '');
      }
    } catch (e) {
      console.log(e);
      notification({
        show: true,
        type: null,
        title: null,
      });
    } finally {
      pageLoading(false);
    }
  };
  useEffect(() => {
    if (asModerator) {
      (async () => {
        try {
          pageLoading(true);
          const result = await api.get(`api/settings/info-moderator`).json<
            IResponse<{
              name: string;
              email: string;
              phone: string;
            } | null>
          >();
          if (_.isNull(result.data)) {
            throw Error();
          }
          form.setValue('name', result.data.name);
          form.setValue('email', result.data.email);
          form.setValue('phone', result.data.phone);
          form.trigger(['name', 'email', 'phone']);
        } catch (e) {
          console.log(e);
          notification({
            show: true,
            type: null,
            title: null,
          });
        } finally {
          pageLoading(false);
        }
      })();
    } else {
      form.setValue('name', '');
      form.setValue('email', '');
      form.setValue('phone', '');
    }
  }, [asModerator]);
  return (
    <FormProvider {...form}>
      <form className="w-full" onSubmit={form.handleSubmit(onSubmit)}>
        <div className="w-full">
          <div className="flex justify-between items-end">
            <h3 className="text-md font-medium text-gray-900">
              Tạo trả lời mới
            </h3>
            <button
              type="button"
              onClick={() => {
                if (asModerator) {
                  setAsModerator(false);
                } else {
                  setAsModerator(true);
                }
              }}
              className="text-xs font-medium text-indigo-600"
            >
              {asModerator
                ? 'Trả lời như người dùng'
                : 'Trả lời với vai trò quản trị viên'}
            </button>
          </div>
          <FormReply
            disabledFields={
              asModerator ? { name: '', email: '', phone: '' } : undefined
            }
          />
          <div className="flex justify-center w-full mt-6">
            <button
              type="submit"
              className="flex w-full justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Tạo trả lời
            </button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default Create;
